import { useState, useEffect } from 'react';
import Sidebar from '../components/Sidebar';
import { api } from '../hooks/useAuth';

export default function FetchByUrl() {
  const [url, setUrl] = useState('');
  const [targetAccount, setTargetAccount] = useState('');
  const [instaSessionId, setInstaSessionId] = useState('');
  const [fetching, setFetching] = useState(false);
  const [message, setMessage] = useState(null);
  const [bulkProgress, setBulkProgress] = useState(null);

  // Poll for bulk progress if we are in processing mode
  useEffect(() => {
    let interval;
    if (bulkProgress && bulkProgress.status === 'processing') {
      interval = setInterval(async () => {
        try {
          const { data } = await api.get('/api/reels/download-from-instagram/bulk-progress');
          setBulkProgress(data);
          if (data.status === 'completed' || data.status === 'idle') {
            clearInterval(interval);
          }
        } catch (err) {
          console.error('Progress check failed:', err);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [bulkProgress]);

  const handleSingleFetch = async (e) => {
    e.preventDefault();
    if (!url) return;
    setFetching(true);
    setMessage(null);

    try {
      const { data } = await api.post('/api/reels/download-from-instagram/fetch-by-url', { 
        url,
        instaSessionId
      });
      
      if (data.new_session_id) {
        setInstaSessionId(data.new_session_id);
        localStorage.setItem('vc_insta_session', data.new_session_id);
      }

      setMessage({ type: 'success', text: `✅ Reel fetched successfully!` });
      setUrl('');
    } catch (err) {
      const errorMsg = err.response?.data?.details || err.response?.data?.error || 'Failed to fetch reel.';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setFetching(false);
    }
  };

  const handleFetchAll = async (e) => {
    e.preventDefault();
    const account = targetAccount;
    if (!account && !instaSessionId) return;
    setFetching(true);
    setMessage(null);
    setBulkProgress(null);

    try {
      const { data } = await api.post('/api/reels/download-from-instagram/fetch-all', { 
        targetUsername: account,
        instaSessionId
      });
      
      if (data.new_session_id) {
        setInstaSessionId(data.new_session_id);
        localStorage.setItem('vc_insta_session', data.new_session_id);
      }

      setBulkProgress({ current: 0, total: data.count, status: 'processing' });
      setMessage({ type: 'success', text: `🚀 ${data.message}` });
      setTargetAccount('');
    } catch (err) {
      const errorMsg = err.response?.data?.details || err.response?.data?.error || 'Failed to bulk fetch.';
      setMessage({ type: 'error', text: errorMsg });
    } finally {
      setFetching(false);
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        <div className="page-header">
          <div>
            <h1 className="page-title">📥 Instagram Direct Fetch</h1>
            <p className="page-subtitle">Fetch by URL (Zero-Login) or Bulk via Session ID</p>
          </div>
        </div>

        {message && (
          <div className={`alert alert-${message.type}`} style={{ marginBottom: 24 }}>
            {message.text}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            
            {/* ZERO LOGIN FETCH */}
            <div className="card">
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>🚀 Fetch by URL (Public)</h3>
              <p style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 20 }}>Paste any public Reel link. No login required for most reels.</p>
              
              <form onSubmit={handleSingleFetch}>
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="https://www.instagram.com/reels/C5pWpGgS6hA/"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    disabled={fetching}
                  />
                </div>
                <button 
                  type="submit" 
                  className={`btn btn-primary ${fetching ? 'loading' : ''}`}
                  disabled={fetching || !url}
                >
                  {fetching ? 'Processing...' : '📥 Fetch Reel Now'}
                </button>
              </form>
            </div>

            {/* BULK FETCH */}
            <div className="card">
              <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>🔥 Bulk Account Fetch</h3>
              <p style={{ fontSize: 13, color: 'var(--text-dim)', marginBottom: 20 }}>Download reels from any profile. (Requires Session ID below)</p>
              
              <form onSubmit={handleFetchAll}>
                <div className="form-group" style={{ marginBottom: 16 }}>
                  <label className="form-label">Target Username</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="e.g. nike, zara"
                    value={targetAccount}
                    onChange={(e) => setTargetAccount(e.target.value)}
                    disabled={fetching}
                  />
                </div>
                <button 
                  type="submit" 
                  className={`btn btn-accent ${fetching ? 'loading' : ''}`}
                  disabled={fetching || (!targetAccount && !instaSessionId)}
                >
                  {fetching ? 'Syncing...' : '🚀 Sync All Reels'}
                </button>
              </form>

              {bulkProgress && (
                <div style={{ marginTop: 20, padding: 15, borderRadius: 12, background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 8 }}>
                    <span style={{ color: 'var(--accent)', fontWeight: 600 }}>
                      {bulkProgress.status === 'completed' ? '✅ Sync Finished' : '🔄 Syncing Reels...'}
                    </span>
                    <span style={{ fontWeight: 700 }}>
                      {bulkProgress.current} / {bulkProgress.total}
                    </span>
                  </div>
                  <div style={{ height: 6, background: 'rgba(255, 255, 255, 0.1)', borderRadius: 3, overflow: 'hidden' }}>
                    <div 
                      style={{ 
                        height: '100%', 
                        background: 'var(--accent)', 
                        width: `${(bulkProgress.current / bulkProgress.total) * 100}%`,
                        transition: 'width 0.5s ease'
                      }} 
                    />
                  </div>
                  {bulkProgress.status === 'completed' && (
                    <p style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 8 }}>
                      All reels are now available in your Reel Manager.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
            {/* SESSION ID CONFIG */}
            <div className="card" style={{ border: '1px solid var(--accent)', background: 'rgba(255, 77, 109, 0.02)' }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 8 }}>🔑 Session Configuration</h3>
              <p style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 15 }}>
                Paste your browser's <b>sessionid</b> cookie here to bypass login blocks and enable private/bulk fetching.
              </p>
              
              <div className="form-group" style={{ marginBottom: 16 }}>
                <input
                  type="text"
                  className="form-input form-input-sm"
                  placeholder="Paste sessionid here..."
                  value={instaSessionId}
                  onChange={(e) => {
                    setInstaSessionId(e.target.value);
                    localStorage.setItem('vc_insta_session', e.target.value);
                  }}
                />
              </div>

              <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.5 }}>
                <strong>How to find it:</strong>
                <ol style={{ margin: '8px 0', paddingLeft: '14px' }}>
                  <li>Login to Instagram on your PC.</li>
                  <li>Press <b>F12</b> {'>'} Application {'>'} Cookies.</li>
                  <li>Find and copy the <b>sessionid</b> value.</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
